import { inject, Injectable, Injector, Type } from '@angular/core';
import { CrudDialogCloseResult } from '@common/defs/crud-dialog-close-result';
import { RecordFormResult } from '@common/defs/record-form-result';
import { BaseDataService } from '@common/services/base-data.service';
import { NotifyService } from '@common/services/notify.service';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class RecordFormDialogService {
  private static readonly DIALOG_OPTIONS: DynamicDialogConfig = {
    styleClass: 'djcer-material-dialog djcer-fullscreen-dialog',
    showHeader: true,
    closable: true,
    width: '80vw',
    contentStyle: { overflow: 'auto' },
    focusOnShow: false,
    focusTrap: false,
    breakpoints: { '960px': '85vw', '640px': '95vw' },
  };

  private readonly injector = inject(Injector);
  private readonly notifyService = inject(NotifyService);

  canOpen(dataService: BaseDataService | undefined): boolean {
    return dataService?.formComponent !== undefined;
  }

  openNew(dataService: BaseDataService, label: string): Promise<RecordFormResult> {
    return this.open(dataService, label, `Novo ${label}`, { onlyFormOnCrud: true });
  }

  openEdit(dataService: BaseDataService, record: Record<string, unknown>, label: string): Promise<RecordFormResult> {
    return this.open(dataService, label, `Editar ${label}`, { onlyFormOnCrud: true, editRecord: record });
  }

  private async open(dataService: BaseDataService, label: string, header: string, data: object): Promise<RecordFormResult> {
    const component = await this.loadForm(dataService, label);
    if (component === null) return { saved: false };
    const ref = this.injector.get(DialogService).open(component, { ...RecordFormDialogService.DIALOG_OPTIONS, header, data });
    if (ref === null) return { saved: false };
    const closeResult: CrudDialogCloseResult | undefined = await firstValueFrom(ref.onClose);
    const saved: boolean = closeResult?.inserted === true;
    if (saved) dataService.refresh();
    return { saved, record: closeResult?.record };
  }

  private async loadForm(dataService: BaseDataService, label: string): Promise<Type<unknown> | null> {
    if (dataService.formComponent === undefined) return null;
    try {
      return await dataService.formComponent();
    } catch (error: unknown) {
      console.error(`Falha ao carregar o formulário de "${label}" (${dataService.constructor.name}):`, error);
      this.notifyService.showToast({ severity: 'error', summary: 'Erro', detail: `Não foi possível abrir o cadastro de ${label}. Tente novamente.` });
      return null;
    }
  }
}
